import { create } from 'zustand'
import type { WeaponClass } from '@/combat/weaponTypes'
import { WEAPONS } from '@/combat/weaponTypes'

export interface CombatState {
  // Player health
  health: number
  maxHealth: number

  // Weapons inventory
  unlockedWeapons: WeaponClass[]
  equippedWeapon: WeaponClass

  // Attack state
  attackCooldown: number     // seconds remaining
  isAttacking: boolean
  attackTimer: number        // animation progress (0-1)

  // Ability state
  abilityCooldown: number
  isAbilityActive: boolean
  abilityTimer: number

  // Special states
  isDodging: boolean
  dodgeTimer: number
  isBlocking: boolean
  isPowerStriking: boolean
  isSteadyAiming: boolean

  // Reload (ranged)
  isReloading: boolean
  reloadTimer: number
  hasAmmo: boolean

  // Actions
  equipWeapon: (weapon: WeaponClass) => void
  unlockWeapon: (weapon: WeaponClass) => void
  startAttack: () => void
  setAttackCooldown: (cd: number) => void
  setAttackTimer: (t: number) => void
  setIsAttacking: (v: boolean) => void
  triggerAbility: () => boolean
  setAbilityCooldown: (cd: number) => void
  setAbilityTimer: (t: number) => void
  setIsAbilityActive: (v: boolean) => void
  setDodging: (v: boolean) => void
  setDodgeTimer: (t: number) => void
  setBlocking: (v: boolean) => void
  setPowerStriking: (v: boolean) => void
  setSteadyAiming: (v: boolean) => void
  setReloading: (v: boolean) => void
  setReloadTimer: (t: number) => void
  setHasAmmo: (v: boolean) => void
  takeDamage: (amount: number) => void
  heal: (amount: number) => void
  resetHealth: () => void
}

export const useCombatStore = create<CombatState>((set, get) => ({
  health: 100,
  maxHealth: 100,

  unlockedWeapons: ['cutlass'],
  equippedWeapon: 'cutlass',

  attackCooldown: 0,
  isAttacking: false,
  attackTimer: 0,

  abilityCooldown: 0,
  isAbilityActive: false,
  abilityTimer: 0,

  isDodging: false,
  dodgeTimer: 0,
  isBlocking: false,
  isPowerStriking: false,
  isSteadyAiming: false,

  isReloading: false,
  reloadTimer: 0,
  hasAmmo: true,

  equipWeapon: (weapon) => {
    const s = get()
    if (s.unlockedWeapons.includes(weapon)) {
      set({
        equippedWeapon: weapon,
        isAttacking: false,
        attackCooldown: 0,
        attackTimer: 0,
        isReloading: false,
        reloadTimer: 0,
        hasAmmo: true,
        isBlocking: false,
        isPowerStriking: false,
        isSteadyAiming: false,
      })
    }
  },

  unlockWeapon: (weapon) => {
    const s = get()
    if (!s.unlockedWeapons.includes(weapon)) {
      set({ unlockedWeapons: [...s.unlockedWeapons, weapon] })
    }
  },

  startAttack: () => {
    const s = get()
    const info = WEAPONS[s.equippedWeapon]
    if (s.attackCooldown > 0 || s.isAttacking || s.isDodging) return
    if (info.category !== 'melee' && !s.hasAmmo) return
    set({ isAttacking: true, attackTimer: 0 })
  },

  setAttackCooldown: (cd) => set({ attackCooldown: cd }),
  setAttackTimer: (t) => set({ attackTimer: t }),
  setIsAttacking: (v) => set({ isAttacking: v }),

  triggerAbility: () => {
    const s = get()
    if (s.abilityCooldown > 0 || s.isDodging) return false
    const info = WEAPONS[s.equippedWeapon]
    set({ isAbilityActive: true, abilityTimer: 0, abilityCooldown: info.abilityCooldown })
    return true
  },

  setAbilityCooldown: (cd) => set({ abilityCooldown: cd }),
  setAbilityTimer: (t) => set({ abilityTimer: t }),
  setIsAbilityActive: (v) => set({ isAbilityActive: v }),
  setDodging: (v) => set({ isDodging: v }),
  setDodgeTimer: (t) => set({ dodgeTimer: t }),
  setBlocking: (v) => set({ isBlocking: v }),
  setPowerStriking: (v) => set({ isPowerStriking: v }),
  setSteadyAiming: (v) => set({ isSteadyAiming: v }),
  setReloading: (v) => set({ isReloading: v }),
  setReloadTimer: (t) => set({ reloadTimer: t }),
  setHasAmmo: (v) => set({ hasAmmo: v }),

  takeDamage: (amount) => {
    const s = get()
    if (s.isDodging) return // invulnerable during dodge
    const reduced = s.isBlocking ? amount * 0.2 : amount
    set({ health: Math.max(0, s.health - reduced) })
  },

  heal: (amount) => {
    const s = get()
    set({ health: Math.min(s.maxHealth, s.health + amount) })
  },

  resetHealth: () => set((s) => ({ health: s.maxHealth })),
}))
